// /**
//  * page-header.tsx — consistent page title + description + actions row.
//  */
// import type { ReactNode } from "react";
// import { cn } from "@/lib/utils";

// export function PageHeader({
//   title,
//   description,
//   actions,
//   className,
// }: {
//   title: string;
//   description?: string;
//   actions?: ReactNode;
//   className?: string;
// }) {
//   return (
//     <div
//       className={cn(
//         "flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between",
//         className,
//       )}
//     >
//       <div className="space-y-1">
//         <h1 className="text-2xl font-bold tracking-tight">{title}</h1>
//         {description && (
//           <p className="text-sm text-muted-foreground">{description}</p>
//         )}
//       </div>
//       {actions && <div className="flex items-center gap-2">{actions}</div>}
//     </div>
//   );
// }

/**
 * page-header.tsx — consistent page title + description + actions row.
 *
 * UI POLISH additions (fully backward-compatible — all new props optional):
 *   • `live`  — when true, renders a <LiveBadge /> inline next to the title.
 *               Use on pages backed by real-time scheduler/API data
 *               (Campaigns, Domains, Dashboard, Autopilot Queue, etc.).
 *   • Typography tightened: text-2xl font-bold → text-xl font-semibold
 *     tracking-tight. Matches Salesforce's subdued page-title treatment
 *     (data is the hero, chrome is quiet).
 *
 * All existing props preserved with identical types and behavior:
 *   title, description, actions, className
 *
 * Existing callers that don't pass `live` receive identical rendering
 * (modulo the font-size/weight change which applies to all callers uniformly).
 *
 * Example — live page:
 *   <PageHeader title="Campaigns" description="All active campaigns." live />
 *
 * Example — existing usage, unchanged behavior:
 *   <PageHeader title="ICP Profiles" description="Manage your ICPs." actions={<Button>Add</Button>} />
 */
import type { ReactNode } from "react";
import { cn } from "@/lib/utils";
import { LiveBadge } from "@/components/ui/live-badge";

export function PageHeader({
  title,
  description,
  actions,
  className,
  live,
}: {
  title: string;
  description?: string;
  actions?: ReactNode;
  className?: string;
  /** When true, shows a pulsing LiveBadge next to the title. */
  live?: boolean;
}) {
  return (
    <div
      className={cn(
        "flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between",
        className,
      )}
    >
      <div className="space-y-1">
        {/* Title row — tighter typography (Salesforce style) */}
        <div className="flex items-center gap-2.5">
          <h1 className="text-xl font-semibold tracking-tight">{title}</h1>
          {live && <LiveBadge />}
        </div>
        {description && (
          <p className="text-sm text-muted-foreground">{description}</p>
        )}
      </div>
      {actions && <div className="flex items-center gap-2">{actions}</div>}
    </div>
  );
}
